// File: /opt/dev_environment/assets/workspace/app-init/src/context/AuthContext.js

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AuthService from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(AuthService.isAuthenticated());
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // ---- Check authentication status on mount ----
  useEffect(() => {
    const checkAuth = async () => {
      if (!AuthService.isAuthenticated()) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        const profile = await AuthService.getProfile();
        setUser(profile);
        setIsAuthenticated(true);
      } catch (error) {
        console.warn('[Auth] Failed to load profile:', error.message);
        // If we can't load profile, tokens might be invalid
        if (error.status === 401) {
          AuthService.clearTokens();
          setIsAuthenticated(false);
          setUser(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // ---- Login ----
  const login = useCallback(async ({ email, password }) => {
    setAuthError(null);
    try {
      await AuthService.login({ email, password });
      const profile = await AuthService.getProfile();
      setUser(profile);
      setIsAuthenticated(true);
      return profile;
    } catch (error) {
      setAuthError(error.message);
      throw error;
    }
  }, []);

  // ---- Register ----
  const register = useCallback(async ({ email, password, full_name }) => {
    setAuthError(null);
    try {
      const result = await AuthService.register({ email, password, full_name });
      return result;
    } catch (error) {
      setAuthError(error.message);
      throw error;
    }
  }, []);

  // ---- Logout ----
  const logout = useCallback(async () => {
    try {
      await AuthService.logout();
    } catch {
      // Ignore logout errors
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setAuthError(null);
    }
  }, []);

  // ---- Refresh user profile ----
  const refreshProfile = useCallback(async () => {
    try {
      const profile = await AuthService.getProfile();
      setUser(profile);
      return profile;
    } catch (error) {
      if (error.status === 401) {
        AuthService.clearTokens();
        setIsAuthenticated(false);
        setUser(null);
      }
      throw error;
    }
  }, []);

  // ---- Clear auth error ----
  const clearError = useCallback(() => {
    setAuthError(null);
  }, []);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    authError,
    login,
    register,
    logout,
    refreshProfile,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;